import { formatLimit } from "@/lib/displayFormat";

// A quote's terms as a broker sees them (PMS-S553's `BrokerCoverageTerms`,
// filled by PMS-S745; BRK-S346): its coverage parts (GL, WC or auto), its
// itemized charges and its totals. Mirrored from the pinned contract; amounts
// are decimal strings (D-10 #3), negative for a credit. The portal shows what
// PMS sends and never sums it into a figure of its own.

export type ChargeCategory =
  | "premium"
  | "tax"
  | "assessment"
  | "fee"
  | "unallocated";
/** Every category: the runtime copy the contract test holds to the pin. */
export const CHARGE_CATEGORIES: readonly ChargeCategory[] = [
  "premium",
  "tax",
  "assessment",
  "fee",
  "unallocated",
];

/** How to read a charge's `rate`: a factor, a percent, or per $100 of its
 *  basis. */
export type RateKind = "factor" | "percent" | "per_100";
export const RATE_KINDS: readonly RateKind[] = ["factor", "percent", "per_100"];

export type CoverageType = "gl" | "wc" | "auto";
export const COVERAGE_TYPES: readonly CoverageType[] = ["gl", "wc", "auto"];

export type PolicyType = "monoline" | "package";
export const POLICY_TYPES: readonly PolicyType[] = ["monoline", "package"];

/** Where a term's value came from. */
export type FieldSource =
  | "carrier"
  | "requested"
  | "program_default"
  | "manual";

/** One line of how the premium is built. `code` is the charge registry's
 *  (`wc.terrorism`) and stays a plain string: new codes arrive. `label` is
 *  what shows; `carrierLabel` is the carrier's own wording, when sent. */
export interface PremiumCharge {
  code: string;
  label: string;
  category: ChargeCategory;
  amount: string;
  rate: string | null;
  rateKind: RateKind | null;
  basisAmount: string | null;
  carrierLabel: string | null;
  coverageType: CoverageType | null;
  state: string | null;
}

/** A coverage part's own economics. */
export interface PremiumBlock {
  grossPremium: string;
  taxes: string;
  otherFees: string;
  agencyFee: string;
  totalCost: string;
}

export interface CoverageTotals {
  totalPremium: string;
  taxesTotal: string;
  otherFeesTotal: string;
  agencyFeeTotal: string;
  taxesAndFees: string;
  totalCost: string;
}

/** A WC class code's rating, as the carrier sent it. `rate` is what it
 *  charges; `baseRate` and `lossCostMultiplier` are the bureau's and the
 *  carrier's (Great American's; null for Pie). */
export interface WcClassLine {
  classCode: string;
  classDescription: string | null;
  state: string;
  payroll: string;
  rate: string | null;
  baseRate: string | null;
  lossCostMultiplier: string | null;
  premium: string;
}

export interface WcCoverageState {
  state: string;
  participation: "rated" | "other_states" | "excluded";
  experienceMod: string | null;
  modEffectiveDate: string | null;
}

export interface WcWaiver {
  scope: "blanket" | "scheduled";
  partyName: string | null;
  partyAddress: {
    street: string;
    addressLine2?: string | null;
    city: string;
    county?: string | null;
    state: string;
    zip: string;
  } | null;
  formNumber: string | null;
  description: string | null;
}

interface WcTerms {
  partOneStatutory: boolean;
  elEachAccident: string | null;
  elDiseaseEachEmployee: string | null;
  elDiseasePolicyLimit: string | null;
  deductibleType:
    | "none"
    | "small_deductible"
    | "large_deductible"
    | "retro"
    | null;
  deductibleAmount: string | null;
  uslhCoverage: boolean | null;
  voluntaryComp: boolean | null;
  foreignVoluntary: boolean | null;
  otherStatesBasis: "none" | "scheduled" | "all_except_excluded";
  governingState: string | null;
}

type WcTermKey = keyof WcTerms | "states" | "waivers";

/** The parts every coverage part shares. */
interface PartBase {
  label: string;
  marketType: "admitted" | "surplus_lines" | null;
  source: FieldSource;
  premium: PremiumBlock;
}

export interface WcCoveragePart extends PartBase {
  coverageType: "wc";
  terms: WcTerms;
  requested?:
    | (WcTerms & { states: WcCoverageState[]; waivers: WcWaiver[] })
    | null;
  fieldSources: Record<WcTermKey, FieldSource>;
  differsFromRequest?: WcTermKey[];
  states: WcCoverageState[];
  waivers: WcWaiver[];
  classLines: WcClassLine[];
}

/** GL and auto fields are read tolerantly by the shared coverage renderer. */
export interface OtherCoveragePart extends PartBase {
  coverageType: "gl" | "auto";
  terms: Record<string, string | boolean | null>;
  requested?: Record<string, string | boolean | null> | null;
  fieldSources: Record<string, FieldSource>;
  differsFromRequest?: string[];
}

export type CoveragePart = WcCoveragePart | OtherCoveragePart;

export interface CoverageTerms {
  policyType: PolicyType;
  coverageParts: CoveragePart[];
  /** Only the codes the registry puts on the broker wire, in PMS's order. */
  charges: PremiumCharge[];
  totals: CoverageTotals;
}

/** The broker's billing choices on a quote, for select & bind (BRK-S209). */
export interface BillingElection {
  billingType: "direct_bill" | "agency_bill" | null;
  paymentMethod:
    | "bank_draft"
    | "echeck"
    | "credit_card"
    | "debit_card"
    | "check"
    | "wire"
    | null;
  paymentPlan:
    | "annual"
    | "quarterly"
    | "monthly"
    | "pay_as_you_go"
    | "semi_annual"
    | "percent_down"
    | "premium_financed"
    | "other"
    | null;
  premiumFinanced: boolean;
  premiumFinance: {
    company: string | null;
    agreementRef: string | null;
    downPayment: string | null;
    installments: number | null;
  } | null;
}

/** The WC class lines across a quote's parts, in PMS's order; none without
 *  terms (a PMS before PMS-S553). */
export function wcClassLines(terms: CoverageTerms | undefined): WcClassLine[] {
  return (terms?.coverageParts ?? []).flatMap((part) =>
    part.coverageType === "wc" ? part.classLines : [],
  );
}

/** How the premium is built: the premium lines, then the taxes, assessments
 *  and fees, each group in PMS's order. */
export function chargeGroups(terms: CoverageTerms | undefined): {
  premium: PremiumCharge[];
  taxesAndFees: PremiumCharge[];
} {
  const charges = terms?.charges ?? [];
  return {
    premium: charges.filter((c) => c.category === "premium"),
    taxesAndFees: charges.filter((c) => c.category !== "premium"),
  };
}

/** A charge's rate, as the carrier applied it ("0.024 per $100 of
 *  $120,000", "2.136%", "× 0.92"); null when it states none. */
export function chargeRate(charge: PremiumCharge): string | null {
  const { rate, rateKind, basisAmount } = charge;
  if (rate === null || rateKind === null) return null;
  const of = basisAmount === null ? "" : ` of ${formatLimit(basisAmount)}`;
  switch (rateKind) {
    case "per_100":
      return `${rate} per $100${of}`;
    case "percent":
      return `${rate}%${of}`;
    case "factor":
      return `× ${rate}`;
  }
}
